import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LngLatBounds } from 'mapbox-gl';
import { useEsriMapContext } from '../EsriMapContext';
import EsriLayerManager from '../layers/EsriLayerManager';
import ServiceManager from '@Map/services/ServiceManager';
import {
	SERVICE_LOADED,
	SERVICE_RELOADED,
	SERVICE_LOADING,
	SERVICES_ALL_LOADED,
	SERVICE_ERROR,
	LOOKUP_COMPLETE,
} from '@Map/services/constants';
import { MIN_ZOOM, RESET_MIN_ZOOM } from '@Map/layers/constants';
import type { LayerConfig } from '../types';
import {
	SourceType,
	type DataServiceType,
	type GeoJsonDataType,
	type ThemeEndpoint,
} from '@Map/services/types';
import type { CompositeLayerProps } from '@Map/layers/types';
import {
	AttributeTypes,
	ColorByOptions,
	type CBAttributes,
	type CBHasValuesPerAttributePerLayer,
	type ConfigLayer,
	type ConfigLayerInternal,
	type LayerToggle,
} from '@Map/panel/types';
import { getColorByTheming } from '@Map/layers/settings';
import type { FitBoundsOptions } from '@Map/types';
import { extractLngLatPositions } from '@Map/extractLngLatPositions';
import type { ValidGeometry } from '@Map/extractLngLatPositions';
import { boundsAreNotInitialized, convertCoordsToBounds } from '@Map/utils';
import EsriUtils from '../utils/esriHelpers';
import esriConfig from '@arcgis/core/config';
import ConfigAdaptor from '@Map/layers/ConfigAdaptor';
import { CONFIG_LOADED, CONFIG_UPDATED } from '@Map/layers/constants';
import { shallowEqual } from '@Map/utils';
import SymbolLoader from '@Map/symbols/SymbolLoader';
import { pictureDataUrlFromSymbolLoader } from '../utils/rendererHelpers';
import { UnitSystem } from '@Components/Inputs/units';

type InspectionType = 'PACP' | 'WSA' | 'MSCC';
type TileType = 'asset' | 'spatial' | 'sensor' | 'inspection';

interface UseLayersOptions {
	dataServices?: DataServiceType<unknown>[];
	/** When provided, layer array is derived from ConfigAdaptor (setLayerConfig-style) instead of service.layers. */
	layerConfig?: ConfigLayer[];
	themeEndpoint?: ThemeEndpoint | null;
	onServiceLoading?: () => void;
	onServicesAllLoaded?: () => void;
	onServiceError?: (criticalError: boolean, serviceId: string) => void;
	onMinZoom?: (minZoom: number) => void;
	onResetMinZoom?: () => void;
	fitBoundsOptions?: FitBoundsOptions;
	/**
	 * When true, auto-fit to data extents on initial load is suppressed.
	 * Set when `position` or `defaultBounds` props are provided so the
	 * consumer's explicit viewport choice is not overridden by data load.
	 */
	hasExternalPosition?: boolean;
	/** Unit system used for range label formatting in the color-by editor. Defaults to Metric. */
	unitSystem?: UnitSystem;
}

const DEFAULT_COLOR_BY_MAX_VALUES = 100;

/**
 * Layer selection + highlight API for syncing useSelection (or other consumers)
 * to EsriLayerManager.
 */
export interface EsriLayerSelectionApi {
	setSelectedItems(layerId: string, ids: string[]): void;
	clearSelection(): void;
	setHighlightedItems(layerId: string, ids: string[]): void;
	clearHighlight(): void;
	getQueryableLayers(): Array<{
		layerId: string;
		systemType: string;
		assetType: string;
	}>;
	getLayerDisplayName(layerId: string): string | undefined;
	getLayerAssetType(layerId: string): string | undefined;
	getLayerSourceId(layerId: string): string | undefined;
	getFeaturePropertiesById(
		layerId: string,
		featureId: string,
	): Record<string, unknown> | undefined;
}

// Maps library-defined special data service types to their canonical assetType string
// so URL-deep-linked selections can be routed to the correct PropertyPane tab even when
// the host app's layer config omits an explicit assetType field.
const SOURCE_TYPE_TO_ASSET_TYPE: Partial<Record<SourceType, string>> = {
	[SourceType.FACILITIES]: 'facility',
	[SourceType.ALERTS]: 'alert',
	[SourceType.ONU]: 'incident',
};

type LayerWithNested = ConfigLayer & {
	layers?: LayerWithNested[];
	virtualLayers?: LayerWithNested[];
};

/**
 * Walks the layer config tree and sets `errored` on every node whose
 * `dataServiceId` matches `serviceId`. Mirrors Mapbox `LayerManager._onServiceError`,
 * which flags all CompositeLayer instances for a failed service (nested or top-level).
 * Returns true if any node was matched.
 */
function flagErroredByServiceId(
	layers: ConfigLayer[],
	serviceId: string,
	errored: boolean,
): boolean {
	let matched = false;
	for (const layer of layers as LayerWithNested[]) {
		if (layer.dataServiceId === serviceId) {
			layer.errored = errored;
			matched = true;
		}
		if (layer.layers?.length) {
			if (flagErroredByServiceId(layer.layers, serviceId, errored)) {
				matched = true;
			}
		}
		if (layer.virtualLayers?.length) {
			if (
				flagErroredByServiceId(layer.virtualLayers, serviceId, errored)
			) {
				matched = true;
			}
		}
	}
	return matched;
}

export const useLayers = (options: UseLayersOptions = {}) => {
	const { view } = useEsriMapContext();
	const managerRef = useRef<EsriLayerManager | null>(null);
	const pendingSelectionsRef = useRef<Map<string, string[]>>(new Map());
	const serviceManagerRef = useRef<ServiceManager | null>(null);
	const configAdaptorRef = useRef<ConfigAdaptor | null>(null);
	const symbolLoaderRef = useRef<SymbolLoader | null>(null);
	const refreshRef = useRef<((zoomToData: boolean) => void) | null>(null);

	// Stabilize themeEndpoint reference: only update when url/token values actually change.
	// Consumers often pass inline objects ({ url, token }) which create new references every
	// render even when the content is identical, which would otherwise re-trigger the effect.
	const stableThemeEndpointRef = useRef(options.themeEndpoint);
	if (!shallowEqual(stableThemeEndpointRef.current, options.themeEndpoint)) {
		stableThemeEndpointRef.current = options.themeEndpoint;
	}
	const stableThemeEndpoint = stableThemeEndpointRef.current;
	const onServiceLoadingRef = useRef(options.onServiceLoading);
	const onServicesAllLoadedRef = useRef(options.onServicesAllLoaded);
	const onServiceErrorRef = useRef(options.onServiceError);
	const onMinZoomRef = useRef(options.onMinZoom);
	const onResetMinZoomRef = useRef(options.onResetMinZoom);
	const fitBoundsOptionsRef = useRef(options.fitBoundsOptions);
	const hasExternalPositionRef = useRef(options.hasExternalPosition);

	// Keep callback refs in sync every render (no deps — always latest)
	onServiceLoadingRef.current = options.onServiceLoading;
	onServicesAllLoadedRef.current = options.onServicesAllLoaded;
	onServiceErrorRef.current = options.onServiceError;
	onMinZoomRef.current = options.onMinZoom;
	onResetMinZoomRef.current = options.onResetMinZoom;
	fitBoundsOptionsRef.current = options.fitBoundsOptions;
	hasExternalPositionRef.current = options.hasExternalPosition;

	/** Callback invoked after every VectorTileLayer loadStyle completion (after onFinalStyleLoaded). */
	const onStyleLoadedRef = useRef<(() => void) | null>(null);
	/** Callback invoked whenever an attribute Lookup finishes loading. */
	const onLookupCompleteRef = useRef<(() => void) | null>(null);
	/** Resolver that bakes the resolved sensor heatmap glow into a style layer at build time. */
	const sensorStyleResolverRef = useRef<
		| ((
				layerId: string,
				styleLayer: Record<string, any>,
		  ) => Record<string, any> | null)
		| null
	>(null);

	/** Skip ConfigAdaptor.updateLayerConfig when layerConfig content is unchanged (avoids async races). */

	/**
	 * True once CONFIG_LOADED has fired for the current ConfigAdaptor instance.
	 * refresh() only executes when both this and servicesAllLoadedRef are true
	 */
	const configLoadedRef = useRef(false);
	/**
	 * True once SERVICES_ALL_LOADED has fired. Paired with configLoadedRef so that
	 * the refresh only fires after both conditions are satisfied, regardless of order.
	 */
	const servicesAllLoadedRef = useRef(false);
	/**
	 * Count of propertyLookup keys across all dataServices that have not yet resolved.
	 * refresh() is gated until this reaches 0 so no loadStyle is fired with a broken
	 * (empty-lookup) filter expression. Decremented on each LOOKUP_COMPLETE event.
	 */
	const pendingLookupsRef = useRef(0);
	/**
	 * True once refresh() has executed at least once to completion. The LOOKUP_COMPLETE
	 * handler uses this to decide whether to pass zoomToData=true (deferred initial paint)
	 * or false (subsequent lookup update after first paint is already done).
	 */
	const initialRefreshDoneRef = useRef(false);
	/**
	 * Signature of the last dataServices payload we observed. Used by the
	 * dataServices effect to detect when the prop is a new array reference but
	 * the same content (common when callers pass an inline `[]` literal that
	 * React re-creates each render) so we don't reset servicesAllLoadedRef /
	 * pendingLookupsRef every render — that reset breaks refresh() because
	 * SERVICES_ALL_LOADED has already fired and won't fire again, leaving
	 * initialRefreshDoneRef stuck at false and pipes never rendering on
	 * inset maps that have no propertyLookup to drive a recovery refresh.
	 */
	const dataServicesSignatureRef = useRef<string>('');
	/**
	 * Signature of the last layerConfig + themeEndpoint we pushed into the
	 * existing ConfigAdaptor. Same problem as dataServicesSignatureRef:
	 * unstable references from the caller would cause the layerConfig effect
	 * to call updateLayerConfig() and re-assign themeEndpoint on every render,
	 * which makes ConfigAdaptor re-fire CONFIG_LOADED and re-fetch the theme
	 * endpoint repeatedly — visible to users as the inset map "clearing and
	 * reloading" in a tight loop.
	 */
	const layerConfigSignatureRef = useRef<string>('');

	// Resolved layer config from ConfigAdaptor after tile metadata loads.
	// Starts as the raw layerConfig prop; updated to the fully-processed
	// ConfigLayerInternal[] when CONFIG_LOADED fires.
	const [resolvedConfig, setResolvedConfig] = useState<ConfigLayer[]>(
		options.layerConfig ?? [],
	);
	/** Mirrors Mapbox PanelControl: per-layer/per-attribute value counts for color-by UI. */
	const [attributeValuesPerLayer, setAttributeValuesPerLayer] =
		useState<CBHasValuesPerAttributePerLayer>({});
	// Bumped whenever layers become queryable so consumers (e.g. useSelection)
	// can re-enrich selectedAssets with assetType derived from layer config.
	const [layersVersion, setLayersVersion] = useState(0);
	// True once CONFIG_LOADED has fired for the current ConfigAdaptor — exposed
	// so ReactEsriMap can wait until ConfigAdaptor is ready before pushing
	// theme edits via editLayer (avoids early-return on null configAdaptorRef).
	const [configLoaded, setConfigLoaded] = useState(false);

	// Fit-to-extents logic in one place so both the effect (refresh) and the returned API can call it.
	const fitToDataExtents = useCallback(() => {
		const sm = serviceManagerRef.current;
		if (!view || !sm) return;

		const services = sm.getAll().filter(service => !service.ignoreExtents);

		// 1. Evaluate geometry bounds from GeoJSON data
		const geometries = services
			.map(service => service.data as GeoJsonDataType)
			.flatMap(data => data?.features?.map(({ geometry }) => geometry))
			.filter((geometry): geometry is ValidGeometry => {
				return !!geometry && 'coordinates' in geometry;
			});

		let geometryBounds: LngLatBounds | null = null;
		if (geometries.length) {
			const coords = extractLngLatPositions(geometries);
			geometryBounds = convertCoordsToBounds(coords);
			if (boundsAreNotInitialized(geometryBounds)) {
				geometryBounds = null;
			}
		}

		// 2. Tile bounds, then add geometry bounds into the combined extent
		const tileBounds = services
			.map(service => service.bounds)
			.filter(Boolean) as LngLatBounds[];

		let allBounds: LngLatBounds | null = null;
		if (geometryBounds) {
			allBounds = new LngLatBounds(
				geometryBounds.getSouthWest(),
				geometryBounds.getNorthEast(),
			);
		}
		for (const bounds of tileBounds) {
			if (!allBounds) {
				allBounds = new LngLatBounds(
					bounds.getSouthWest(),
					bounds.getNorthEast(),
				);
			} else {
				allBounds.extend(bounds);
			}
		}

		// 3. Go to the extent
		if (!allBounds || boundsAreNotInitialized(allBounds)) return;
		const fitBoundsOptions = fitBoundsOptionsRef.current;
		if (allBounds.getNorth() === allBounds.getSouth()) {
			const target = EsriUtils.lngLatToPoint(allBounds.getCenter());
			view.goTo({
				target,
				zoom: fitBoundsOptions?.zoom ?? 15,
			});
		} else {
			const promise = view.goTo(EsriUtils.boundsToExtent(allBounds));
			if (fitBoundsOptions?.maxZoom !== undefined) {
				const { maxZoom } = fitBoundsOptions;
				void promise.then(() => {
					if (view.zoom > maxZoom) {
						void view.goTo({ zoom: maxZoom }, { duration: 0 });
					}
				});
			}
		}
	}, [view]);

	// Instantiate both managers once when view is ready.
	// The ServiceManager lives here so its lifecycle is tied to the view, not
	// to the dataServices prop — preventing the manager from being torn down
	// and recreated on every render when the consumer passes a new array ref.
	useEffect(() => {
		if (!view) return;

		const manager = new EsriLayerManager(view);
		managerRef.current = manager;

		// After every loadStyle completion, force-refresh each layer's visual state
		// from its current `active` flag. We can't use setLayerVisibility here because
		// its `active !== visible` guard always skips: active is set synchronously
		// during the toggle, but getStyleLayer returns null while loadStyle is in
		// flight (especially with large datasets), so the visual update was silently
		// dropped. forceRefreshAllVisibility bypasses the guard and always re-applies.
		manager.onFinalStyleLoaded = () => {
			manager.forceRefreshAllVisibility();
			onStyleLoadedRef.current?.();
		};

		// Let the theme layer bake the resolved sensor heatmap glow into the style
		// as it's built (read live from the ref so the latest pending edits apply).
		manager.sensorStyleResolver = (layerId, styleLayer) =>
			sensorStyleResolverRef.current?.(layerId, styleLayer) ?? null;

		// Drain any selections that were queued before the manager was ready.
		pendingSelectionsRef.current.forEach((ids, layerId) => {
			manager.setSelectedItemsOnLayer(layerId, ids);
		});
		pendingSelectionsRef.current.clear();

		const sm = new ServiceManager();
		serviceManagerRef.current = sm;

		const credentialInterceptor = {
			before(params: {
				url: string;
				requestOptions: { withCredentials?: boolean };
			}) {
				const originsRequiringCredentials = sm
					.getAll()
					.filter(s => s.requiresCredentials && s.baseUrl != null)
					.map(s => new URL(s.baseUrl as string).origin);

				if (
					params.url.includes(window.location.host) ||
					originsRequiringCredentials.some(origin =>
						params.url.startsWith(origin),
					)
				) {
					params.requestOptions.withCredentials = true;
				}
			},
		};
		esriConfig.request.interceptors?.push(credentialInterceptor);

		const refresh = (zoomToData: boolean) => {
			// Don't fire a loadStyle until all propertyLookups have resolved.
			// Firing with an empty Lookup bakes a broken filter into the style spec and
			// races against the corrective LOOKUP_COMPLETE refresh. See pendingLookupsRef.
			if (pendingLookupsRef.current > 0) return;
			// Don't run convertForLayers() while the ConfigAdaptor is mid-update.
			// updateLayerConfig() rebuilds _layerConfig synchronously but defers the
			// _configMap rebuild to loadExternalConfigs() (async). Running
			// convertForLayers() in this window uses the new _layerConfig against the
			// old _configMap, silently excluding new/updated layers. _deleteNotFound()
			// then removes those layers from _layers, and if a concurrent dataServices
			// reset raises pendingLookupsRef above 0, the subsequent CONFIG_LOADED
			// refresh is also gated — leaving layers permanently absent until the next
			// successful refresh cycle. CONFIG_LOADED will re-trigger refresh() with
			// the fresh _configMap once the update is complete.
			if (configAdaptorRef.current != null && !configLoadedRef.current)
				return;

			const configAdaptor = configAdaptorRef.current;
			const useLayerConfig = configAdaptor != null;

			// Collect styleSources from all loaded services so the vector tile layer has correct sources.
			const allSources: any[] = [];
			for (const service of sm.getAll()) {
				if (!service.loaded) continue;
				const items = (service.dataSources ?? []).map(src => {
					const source = { ...src.source } as any;
					if (
						!Object.prototype.hasOwnProperty.call(source, 'minzoom')
					) {
						source.minzoom = 0;
					}
					return { [src.id]: source };
				});
				allSources.push(...items);
			}

			if (useLayerConfig) {
				const layers =
					configAdaptor.convertForLayers() as CompositeLayerProps[];

				if (layers.length === 0) {
					return;
				}
				// Set styleSources AFTER addMany so when the setter triggers _updateEsriLayers,
				// the layers are already in _layers and both conditions are met in one shot.
				manager.addMany(layers);
				if (allSources.length) {
					manager.styleSources = allSources;
				}
			} else {
				for (const service of sm.getAll()) {
					if (!service.loaded) continue;
					const layers =
						(service.layers as CompositeLayerProps[]) ?? [];
					manager.addMany(layers, service.id);
				}
				if (allSources.length) {
					manager.styleSources = allSources;
				}
			}

			if (zoomToData && !hasExternalPositionRef.current) {
				fitToDataExtents();
			}
			initialRefreshDoneRef.current = true;
		};
		refreshRef.current = refresh;

		sm.on(SERVICE_LOADED, () => {});
		sm.on(SERVICE_RELOADED, () => {
			refresh(false);
		});
		sm.on(SERVICE_LOADING, () => {
			onServiceLoadingRef.current?.();
		});
		sm.on(SERVICES_ALL_LOADED, () => {
			servicesAllLoadedRef.current = true;
			if (!configAdaptorRef.current || configLoadedRef.current) {
				refresh(true);
				setLayersVersion(v => v + 1);
			}
			onServicesAllLoadedRef.current?.();
		});
		sm.on<{ criticalError: boolean; serviceId: string }>(
			SERVICE_ERROR,
			({ criticalError, serviceId }) => {
				const adaptor = configAdaptorRef.current;
				if (adaptor) {
					const layerConfig = adaptor.serializeLayerConfig();
					const matched = flagErroredByServiceId(
						layerConfig,
						serviceId,
						criticalError,
					);
					if (matched) {
						adaptor.updateLayerConfig(layerConfig);
						setLayersVersion(v => v + 1);
					}
				}
				onServiceErrorRef.current?.(criticalError, serviceId);
			},
		);
		// When a property lookup resolves, rebuild the VTL style so that
		// property-based filters (e.g. rehab: { rehab0: 'CIPP' } → { id: [...] })
		// are applied with the now-available id list. Without this call the filter
		// stays as ['==', ['get', 'rehab0'], 'CIPP'] which never matches tile
		// features (tiles only expose 'id'), so rehab layers never render.
		// Also notify color-by-attribute subscribers that need to rebuild
		// id → value match expressions (handled via onLookupCompleteRef).
		sm.on<{ serviceId?: string; key?: string }>(
			LOOKUP_COMPLETE,
			() => {
				pendingLookupsRef.current = Math.max(
					0,
					pendingLookupsRef.current - 1,
				);
				// If no refresh has yet completed (gate suppressed CONFIG_LOADED / SERVICES_ALL_LOADED),
				// this is the deferred initial paint — honor the zoom-to-data behavior those
				// handlers would have performed.
				refresh(!initialRefreshDoneRef.current);
				onLookupCompleteRef.current?.();
			},
		);

		return () => {
			const interceptors = esriConfig.request.interceptors;
			const idx = interceptors?.indexOf(credentialInterceptor) ?? -1;
			if (idx !== -1) interceptors?.splice(idx, 1);
			refreshRef.current = null;
			if (configAdaptorRef.current) {
				configAdaptorRef.current.destroy();
				configAdaptorRef.current = null;
			}
			configLoadedRef.current = false;
			servicesAllLoadedRef.current = false;
			symbolLoaderRef.current = null;
			manager.deleteAll();
			sm.deleteAll();
			managerRef.current = null;
			serviceManagerRef.current = null;
		};
	}, [view, fitToDataExtents]);

	// When layerConfig is provided, create or update ConfigAdaptor so layer array comes from
	// convertForLayers() (setLayerConfig-style) instead of service.layers. Runs after the
	// effect that creates ServiceManager so serviceManagerRef.current is set.
	useEffect(() => {
		if (!view) return;
		const sm = serviceManagerRef.current;
		if (!sm) return;

		const layerConfig = options.layerConfig;
		const themeEndpoint = stableThemeEndpoint ?? null;

		if (layerConfig?.length) {
			const symbolLoader = symbolLoaderRef.current ?? new SymbolLoader();
			if (!symbolLoaderRef.current)
				symbolLoaderRef.current = symbolLoader;
			const existing = configAdaptorRef.current;
			// Content signature lets us skip the updateLayerConfig path when
			// the caller passes a fresh reference with the same content. The
			// signature includes themeEndpoint so a real theme switch still
			// triggers the rebuild.
			const layerConfigSignature = `${JSON.stringify(themeEndpoint)}::${JSON.stringify(
				layerConfig,
			)}`;
			if (existing) {
				if (layerConfigSignature !== layerConfigSignatureRef.current) {
					configLoadedRef.current = false;
					setConfigLoaded(false);
					existing.updateLayerConfig(layerConfig);
					if (themeEndpoint !== null)
						existing.themeEndpoint = themeEndpoint;
					layerConfigSignatureRef.current = layerConfigSignature;
				}
			} else {
				layerConfigSignatureRef.current = layerConfigSignature;
				const configAdaptor = new ConfigAdaptor(
					layerConfig,
					symbolLoader,
					sm,
					themeEndpoint,
					DEFAULT_COLOR_BY_MAX_VALUES,
					null,
					options.unitSystem ?? UnitSystem.Metric,
					null,
				);
				configAdaptorRef.current = configAdaptor;
				const syncAdaptorPanelState = () => {
					setResolvedConfig(configAdaptor.convertForPanel());
					setAttributeValuesPerLayer({
						...configAdaptor.cbHasValuesPerAttributePerLayer,
					});
				};
				configAdaptor.on(CONFIG_LOADED, () => {
					if (!refreshRef.current) {
						console.warn(
							'[inno-map] CONFIG_LOADED fired but layer refresh function is not yet registered — layers may not be applied.',
						);
					}
					configLoadedRef.current = true;
					setConfigLoaded(true);
					if (servicesAllLoadedRef.current) {
						refreshRef.current?.(true);
						setLayersVersion(v => v + 1);
					}
					syncAdaptorPanelState();
				});
				configAdaptor.on(CONFIG_UPDATED, syncAdaptorPanelState);
				configAdaptor.on<{ minZoom: number }>(
					MIN_ZOOM,
					({ minZoom }) => {
						onMinZoomRef.current?.(minZoom);
					},
				);
				configAdaptor.on(RESET_MIN_ZOOM, () => {
					onResetMinZoomRef.current?.();
				});
			}
		} else if (configAdaptorRef.current) {
			configAdaptorRef.current.destroy();
			configAdaptorRef.current = null;
			configLoadedRef.current = false;
			setConfigLoaded(false);
			setAttributeValuesPerLayer({});
		}
	}, [view, options.layerConfig, stableThemeEndpoint]);

	// When dataServices change, push them into the ServiceManager.
	// addMany handles add/update/remove diffing internally so repeated calls
	// with the same content are safe and won't re-trigger fetches.
	// Reset servicesAllLoadedRef so that a stale true value from a previous
	// service set does not trigger a premature refresh before the new
	// services have finished loading.
	useEffect(() => {
		const sm = serviceManagerRef.current;
		if (!sm || !options.dataServices?.length) return;
		// Build a content signature so we only reset state-tracking refs when
		// the dataServices payload genuinely changed. Callers that pass an
		// inline array literal (or an unmemoized selector result) hand us a
		// fresh reference each render — addMany() is a no-op for that, but
		// resetting servicesAllLoadedRef / pendingLookupsRef on every render
		// previously made refresh() unreachable on inset maps after the
		// initial SERVICES_ALL_LOADED fired.
		const signature = options.dataServices
			.map(
				d =>
					`${d.id}:${Object.keys(d.propertyLookup ?? {})
						.sort()
						.join(',')}`,
			)
			.sort()
			.join('|');
		if (signature !== dataServicesSignatureRef.current) {
			servicesAllLoadedRef.current = false;
			initialRefreshDoneRef.current = false;
			dataServicesSignatureRef.current = signature;
			// Add services first, then count only NEW pending lookups. The
			// previous code counted every lookup key in dataServices, but
			// DataService.lookupProperties() skips keys whose Lookup already
			// exists from a prior addMany call. Those already-complete lookups
			// would never re-fire LOOKUP_COMPLETE, so the counter stayed > 0
			// forever and refresh() bailed on the `if (pending > 0) return`
			// gate. By querying the live service for each lookup's data we
			// count only the in-flight ones — each of which will fire
			// LOOKUP_COMPLETE exactly once.
			sm.addMany(options.dataServices);
			pendingLookupsRef.current = options.dataServices.reduce(
				(total, ds) => {
					const service = sm.getById(ds.id);
					const lookupKeys = Object.keys(ds.propertyLookup ?? {});
					if (!service) return total + lookupKeys.length;
					return (
						total +
						lookupKeys.filter(key => {
							const lookup = service.getLookup?.(key);
							return !lookup || lookup.data === undefined;
						}).length
					);
				},
				0,
			);
		} else {
			sm.addMany(options.dataServices);
		}
	}, [options.dataServices, view]);

	const setSelectedItems = useCallback((layerId: string, ids: string[]) => {
		if (managerRef.current) {
			managerRef.current.setSelectedItemsOnLayer(layerId, ids);
		} else {
			pendingSelectionsRef.current.set(layerId, ids);
		}
	}, []);
	const clearSelectionOnLayers = useCallback(() => {
		managerRef.current?.clearSelectedLayers();
	}, []);
	const getQueryableLayers = useCallback(
		() =>
			managerRef.current?.loadedLayers
				.filter(l => l.layerInfo.systemType && l.layerInfo.assetType)
				.map(l => ({
					layerId: l.id,
					systemType: l.layerInfo.systemType!,
					assetType: l.layerInfo.assetType!,
				})) ?? [],
		[],
	);

	const setHighlightedItems = useCallback(
		(layerId: string, ids: string[]) => {
			managerRef.current?.setHighlightedItemsOnLayer(layerId, ids);
		},
		[],
	);
	const clearHighlightOnLayers = useCallback(() => {
		managerRef.current?.clearHighlightedLayers();
	}, []);

	const getLayerDisplayName = useCallback(
		(layerId: string): string | undefined =>
			managerRef.current?.layers.find(l => l.id === layerId)?.displayName,
		[],
	);

	const getLayerAssetType = useCallback(
		(layerId: string): string | undefined => {
			const layer = managerRef.current?.getLayerById(layerId);
			if (!layer) return undefined;
			if (layer.layerInfo.assetType) return layer.layerInfo.assetType;
			// Spatial overlays are represented as "tiles-other" style layers and do
			// not always carry an explicit assetType in layerInfo.
			if (layer.layerInfo.source === 'tiles-other') return 'amSpatial';
			const service = serviceManagerRef.current?.getById(
				layer.serviceId ?? '',
			);
			const sourceType = service?.type;
			return sourceType
				? SOURCE_TYPE_TO_ASSET_TYPE[sourceType]
				: undefined;
		},
		[layersVersion],
	);

	const getLayerSourceId = useCallback(
		(layerId: string): string | undefined =>
			managerRef.current?.getLayerById(layerId)?.layerInfo.source,
		[layersVersion],
	);

	const getFeaturePropertiesById = useCallback(
		(
			layerId: string,
			featureId: string,
		): Record<string, unknown> | undefined => {
			const layer = managerRef.current?.getLayerById(layerId);
			const service = serviceManagerRef.current?.getById(
				layer?.serviceId ?? '',
			);
			const allDataSources = service?.dataSources;
			if (!allDataSources?.length) return undefined;
			// A service can bundle sibling dataSources for other asset types
			// (e.g. Manholes and Pipes under one shared service). Scope the
			// search to the querying layer's own sourceLayer so an id that
			// collides across asset types doesn't backfill with the wrong
			// asset's properties.
			const dataSources = layer?.layerInfo.sourceLayer
				? allDataSources.filter(
						ds => ds.sourceLayer === layer.layerInfo.sourceLayer,
				  )
				: allDataSources;
			for (const ds of dataSources) {
				const feat = ds.data?.features?.find(
					f =>
						(f.properties as { id?: string } | undefined)?.id ===
						featureId,
				);
				if (feat?.properties) {
					return feat.properties as Record<string, unknown>;
				}
			}
			return undefined;
		},
		[layersVersion],
	);

	const layerSelectionApi = useMemo<EsriLayerSelectionApi>(
		() => ({
			setSelectedItems,
			clearSelection: clearSelectionOnLayers,
			setHighlightedItems,
			clearHighlight: clearHighlightOnLayers,
			getQueryableLayers,
			getLayerDisplayName,
			getLayerAssetType,
			getLayerSourceId,
			getFeaturePropertiesById,
		}),
		[
			setSelectedItems,
			clearSelectionOnLayers,
			setHighlightedItems,
			clearHighlightOnLayers,
			getQueryableLayers,
			getLayerDisplayName,
			getLayerAssetType,
			getLayerSourceId,
			getFeaturePropertiesById,
		],
	);

	const getLayerIdsByServiceId = useCallback(
		(serviceId: string): string[] => {
			if (!serviceId) return [];
			return (
				managerRef.current
					?.getByServiceId(serviceId)
					.map(layer => layer.id) ?? []
			);
		},
		[],
	);

	/**
	 * Keeps ConfigAdaptor layer edits in sync with the theme editor (see Mapbox
	 * PanelControl._onEditLayer). Required so color-by-attribute fetches value
	 * lists, populates virtual sub-layers, and updates attribute value counts.
	 */
	const editLayer = useCallback(
		(layerId: string, edits: ConfigLayerInternal) => {
			const adaptor = configAdaptorRef.current;
			if (!adaptor) return;
			void adaptor.editLayer(layerId, edits);
			// Refresh resolvedConfig so iconImage and layerName changes are reflected
			// in the layer panel immediately without waiting for CONFIG_UPDATED.
			setResolvedConfig(adaptor.convertForPanel());
		},
		[],
	);

	const clearAllLayerEdits = useCallback(() => {
		configAdaptorRef.current?.clearLayerChanges();
	}, []);

	/**
	 * Persists layer-panel visibility toggles into ConfigAdaptor visibility state.
	 * This keeps Esri behavior aligned with Mapbox PanelControl and prevents
	 * hidden layers from reappearing after async config/service refresh cycles.
	 */
	const setConfigVisibility = useCallback(
		(toggle: Pick<LayerToggle, 'layerPath' | 'state'>) => {
			const adaptor = configAdaptorRef.current;
			if (!adaptor) return;
			adaptor.setVisbilityOfLayers({
				layerPath: toggle.layerPath,
				state: toggle.state,
				hiddenLayers: [],
			});
			setResolvedConfig(adaptor.convertForPanel());
		},
		[],
	);

	/**
	 * Builds a Mapbox-style color expression for a tile-service VectorTileLayer
	 * style layer. Vector tile features for Info360 services typically expose
	 * only `id`; the actual attribute (e.g. `nominalDiameter`) lives in a
	 * separate id → value lookup that ConfigAdaptor fetches via
	 * addAttributeLookup. This helper resolves that lookup and emits a single
	 * `match` expression keyed on `['get', 'id']` that maps each id to the
	 * color of its range / value.
	 *
	 * Returns null if a `step`/`match` on the raw attribute should be used
	 * instead (e.g. ONU/GeoJSON layers where the attribute is on the feature).
	 */
	const getTileColorExpression = useCallback(
		(
			layerId: string,
			edits: Partial<ConfigLayer>,
			fallbackColor: string,
		): unknown[] | null => {
			const adaptor = configAdaptorRef.current;
			const sm = serviceManagerRef.current;
			if (!adaptor || !sm) return null;

			const configLayer = adaptor.getLayer(layerId);
			if (!configLayer) return null;

			const service = sm.getById(configLayer.dataServiceId);
			if (!service) return null;

			// Range-forced services (e.g. sensors) expose a single implicit attribute
			// via `attributeForRange`; fall back to it when an edit arrives without an
			// explicit `attribute` so the render path stays robust (defense-in-depth —
			// the editor also supplies it on color-by change).
			const attributeForRange = (configLayer as any).attributeForRange;
			const attribute =
				edits.attribute ??
				configLayer.configWithEdits.attribute ??
				attributeForRange;
			if (!attribute) return null;

			const lookupKey = `${configLayer.attributesLayerId}~${attributeForRange ?? attribute}`;
			const lookup = service.getLookup(lookupKey);

			if (!lookup?.data) return null;

			const colorRange = edits.colorRange ?? configLayer.configWithEdits.colorRange;
			if (!colorRange?.length) return null;

			// Use getColorByTheming semantics: attributeType='range' implies range
			// coloring regardless of what colorBy says. This mirrors the logic in
			// ConfigAdaptor._checkForAttributes (which triggers _getAttributeRange
			// based on colorByTheming, not the raw colorBy field).
			const mergedConfig = {
				...configLayer.configWithEdits,
				...edits,
			} as ConfigLayerInternal;
			const colorBy = getColorByTheming(mergedConfig);

			// Group feature IDs by color to emit a compact match expression.
			// Lookup keys are in the format "<layerId>#<assetId>" (e.g. "pipe#Wtr-Mn-1"),
			// which matches the 'id' property on the VTL tile features — do NOT strip the prefix.
			const idsByColor = new Map<string, string[]>();
			const pushIds = (color: string, ids: string[]) => {
				if (!ids.length) return;
				const existing = idsByColor.get(color);
				if (existing) {
					existing.push(...ids);
				} else {
					idsByColor.set(color, [...ids]);
				}
			};

			if (colorBy === ColorByOptions.range) {
				// Half-open ranges: [value, nextValue - 0.0001] avoids double-coloring
				// boundary values when adjacent ranges share an endpoint.
				colorRange.forEach(({ value, color }, i, arr) => {
					const min = Number(value);
					const next = arr[i + 1]?.value;
					const max =
						next != null
							? Number(next) - 0.0001
							: Number.MAX_SAFE_INTEGER;
					pushIds(color, lookup.findInRange(min, max));
				});
			} else if (colorBy === ColorByOptions.attribute) {
				colorRange.forEach(({ value, color }) => {
					pushIds(color, lookup.findMatch(value as any));
				});
			} else {
				return null;
			}

			// Build a step expression on the tile feature's own attribute value.
			// This covers pipes whose nominalDiameter (or similar) is embedded
			// directly in the tile features but not present in the lookup service.
			// The step uses the same range breaks as the lookup so the two
			// approaches produce identical colours.
			let stepFallback: unknown = fallbackColor;
			if (colorBy === ColorByOptions.range) {
				// ['to-number', ['get', attr], -1] converts null → -1 so that
				// features without the attribute always land in the fallback bucket
				// (any range start must be >= 0).
				const stepExpr: unknown[] = [
					'step',
					['to-number', ['get', attribute as string], -1],
					fallbackColor,
				];
				colorRange.forEach(({ value, color }) =>
					stepExpr.push(Number(value), color),
				);
				stepFallback = stepExpr;
			}

			if (idsByColor.size === 0) {
				// No lookup hits at all — return the step expression so that tile
				// features that carry the attribute directly still get coloured.
				return colorBy === ColorByOptions.range
					? (stepFallback as unknown[])
					: null;
			}

			// Mapbox GL match expression keyed on feature 'id'.
			// The fallback is the step expression (not a flat colour) so that
			// pipes absent from the lookup but present in tile features still
			// get correctly ranged colours.
			const expr: unknown[] = ['match', ['get', 'id']];
			idsByColor.forEach((ids, color) => {
				expr.push(ids.length === 1 ? ids[0] : ids, color);
			});
			expr.push(stepFallback);
			return expr;
		},
		[],
	);

	/**
	 * True when this layer's colour-by depends on an id → value lookup that has
	 * not yet loaded. `useTheme` uses this to skip the degenerate pre-lookup
	 * symbol pass — applying a colour-by before the lookup resolves would point
	 * the symbol's `icon-image` at recoloured sprites not yet in the atlas,
	 * blanking the icons. Returns false for layers with no lookup configured (the
	 * attribute lives on the tile features) so those still colour immediately.
	 */
	const isTileLookupPending = useCallback(
		(layerId: string, edits: Partial<ConfigLayer>): boolean => {
			const adaptor = configAdaptorRef.current;
			const sm = serviceManagerRef.current;
			if (!adaptor || !sm) return false;

			const configLayer = adaptor.getLayer(layerId);
			if (!configLayer) return false;

			const service = sm.getById(configLayer.dataServiceId);
			if (!service) return false;

			const attributeForRange = (configLayer as any).attributeForRange;
			const attribute =
				edits.attribute ??
				configLayer.configWithEdits.attribute ??
				attributeForRange;
			if (!attribute) return false;

			const lookupKey = `${configLayer.attributesLayerId}~${attributeForRange ?? attribute}`;
			const lookup = service.getLookup(lookupKey);

			// Registered placeholder → pending iff its data hasn't populated yet.
			if (lookup) return !lookup.data;
			// Not yet registered → pending only for range-forced services (the
			// lookup is on its way); a genuine attribute-in-tiles symbol layer has
			// no attributeForRange, so it is not pending and colours immediately.
			return attributeForRange != null;
		},
		[],
	);

	/** Registers a callback that fires when any attribute Lookup finishes loading. */
	const setOnLookupComplete = useCallback((cb: (() => void) | null) => {
		onLookupCompleteRef.current = cb;
	}, []);

	return {
		// Fully-processed layer config for the panel (populated after tile metadata loads).
		resolvedConfig,
		// True once CONFIG_LOADED has fired — use to gate editLayer calls that
		// must not run until ConfigAdaptor is ready (e.g. initial theme restore).
		configLoaded,
		/** For EditorProvider: discrete value counts per attribute (color-by UI). */
		attributeValuesPerLayer,

		// -----------------------------------------------------------------------
		// Generic layer management
		// -----------------------------------------------------------------------

		addLayer(config: LayerConfig, index?: number): void {
			if (!view?.map) return;
			const esriMap = view.map;
			import('@arcgis/core/layers/FeatureLayer').then(
				({ default: FeatureLayer }) => {
					const layer = new FeatureLayer({
						id: config.id,
						url: config.url,
						title: config.title,
						visible: config.visible ?? true,
						opacity: config.opacity ?? 1,
						definitionExpression: config.definitionExpression,
					});
					esriMap.add(layer, index);
				},
			);
		},

		removeLayer(layerId: string): void {
			if (!view?.map) return;
			const layer = view.map.findLayerById(layerId);
			if (layer) view.map.remove(layer);
		},

		setLayerVisibility(layerId: string, visible: boolean): void {
			if (!view?.map) return;
			const layer = view.map.findLayerById(layerId);
			if (layer) {
				layer.visible = visible;
			} else {
				managerRef.current?.toggleLayer(layerId, visible);
			}
		},

		syncVisibility(layerVisibility: Record<string, boolean>): void {
			managerRef.current?.setLayerVisibility(layerVisibility);
		},

		/**
		 * Registers a callback that fires after every VectorTileLayer loadStyle
		 * completes (chained after onFinalStyleLoaded / forceRefreshAllVisibility).
		 * Pass null to deregister. ReactEsriMap uses this to re-apply colorBy
		 * expressions that were silently skipped while the style was loading.
		 */
		setOnStyleLoaded(cb: (() => void) | null): void {
			onStyleLoadedRef.current = cb;
		},

		/**
		 * Registers a resolver that rewrites a style layer as the VTL style is
		 * built (used to bake the resolved sensor heatmap glow into the loaded
		 * style so it never flickers through the asset icon). Pass null to clear.
		 */
		setSensorStyleResolver(
			fn:
				| ((
						layerId: string,
						styleLayer: Record<string, any>,
				  ) => Record<string, any> | null)
				| null,
		): void {
			sensorStyleResolverRef.current = fn;
		},

		setDefinitionExpression(layerId: string, expression: string): void {
			if (!view?.map) return;
			const esriMap = view.map;
			import('@arcgis/core/layers/FeatureLayer').then(
				({ default: FeatureLayer }) => {
					const layer = esriMap.findLayerById(layerId);
					if (layer && layer instanceof FeatureLayer) {
						layer.definitionExpression = expression;
					}
				},
			);
		},

		setFilter(layerId: string, filter: boolean | string[]): void {
			managerRef.current?.getLayerById(layerId)?.setFilter(filter);
		},

		addCompositeLayer(props: CompositeLayerProps): void {
			managerRef.current?.addMany([props]);
		},

		addCompositeLayers(
			props: CompositeLayerProps[],
			serviceId?: string,
		): void {
			managerRef.current?.addMany(props, serviceId);
		},

		setSelectedItems,
		setHighlightedItems,
		layerSelectionApi,
		getLayerIdsByServiceId,

		clearSelection: clearSelectionOnLayers,

		clearHighlight: clearHighlightOnLayers,

		// -----------------------------------------------------------------------
		// System / spatial layers
		// -----------------------------------------------------------------------

		loadSystemLayer(_systemType: string, _config: LayerConfig): void {
			// TODO: create FeatureLayer with system-type-specific renderer + popup
		},

		loadSpatialData(_config: LayerConfig): void {
			// TODO: create FeatureLayer for spatial data overlays
		},

		// -----------------------------------------------------------------------
		// Asset display layers
		// -----------------------------------------------------------------------

		loadAlerts(_config: LayerConfig): void {
			// TODO: FeatureLayer with alert symbol set
		},

		loadIncidents(_config: LayerConfig): void {
			// TODO: FeatureLayer with incident symbol set
		},

		loadFacilities(_config: LayerConfig): void {
			// TODO: FeatureLayer with facility symbol set
		},

		loadSensors(_config: LayerConfig): void {
			// TODO: FeatureLayer with sensor symbol set
		},

		// -----------------------------------------------------------------------
		// Inspection layers
		// -----------------------------------------------------------------------

		loadInspectionLayer(_type: InspectionType, _config: LayerConfig): void {
			// TODO: FeatureLayer configured per inspection standard (PACP / WSA / MSCC)
		},

		// -----------------------------------------------------------------------
		// Risk / rehab models
		// -----------------------------------------------------------------------

		loadRiskModel(_modelId: string): void {
			// TODO: fetch risk model config, apply renderer to matching layer
		},

		loadRehabModel(_modelId: string): void {
			// TODO: fetch rehab model config, apply renderer to matching layer
		},

		// -----------------------------------------------------------------------
		// Tile regeneration
		// -----------------------------------------------------------------------

		async regenerateTiles(_type: TileType): Promise<void> {
			// TODO: call tile regen API endpoint for the given type
		},

		// -----------------------------------------------------------------------
		// Viewport / fit to data
		// -----------------------------------------------------------------------

		/**
		 * Fits the view to the extent of loaded services (excluding those with
		 * ignoreExtents). Uses service bounds when available, otherwise
		 * geometries from service data. Mirrors index-esri _fitToExtents.
		 */
		fitToDataExtents,

		/**
		 * Regenerates the sprite sheet for the VectorTileLayer that owns
		 * `layerId`, using the VTL's current (live) icon-image values.
		 * Call this after updating icon-image color via setStyleLayer so
		 * the sheet matches the new sprite IDs.
		 */
		refreshSymbolSprites(layerId: string): Promise<void> {
			return (
				managerRef.current?.refreshSpritesForVtlContainingLayer(layerId) ??
				Promise.resolve()
			);
		},

		/**
		 * Ensures the VTL atlas contains the supplied synthetic style layers'
		 * sprites BEFORE the caller flips the live `icon-image` — the atlas-first
		 * ordering that makes symbol colour-by transition with no blank frame.
		 */
		ensureSymbolSprites(layerId: string, syntheticStyleLayers: any[]): Promise<void> {
			return (
				managerRef.current?.ensureSpritesForStyleLayers(
					layerId,
					syntheticStyleLayers,
				) ?? Promise.resolve()
			);
		},

		/** Expose manager for advanced consumers (e.g. storybook debugging). */
		get manager(): EsriLayerManager | null {
			return managerRef.current;
		},

		/**
		 * Returns the SVG data URL for the given icon key + color combination,
		 * using the same SymbolLoader instance used to build the sprite atlas.
		 * Returns an empty string when the loader is not yet initialised or the
		 * icon key is unknown.  Used by ReactEsriMap to regenerate `iconImage`
		 * in pendingEdits when the user changes a layer's theme color so the
		 * LayerPanel swatch reflects the new color immediately.
		 */
		getIconSvg(icon: string, color: string): string {
			const loader = symbolLoaderRef.current;
			if (!loader) return '';
			return loader.getSvg(SymbolLoader.createSymbolId(icon, color)) ?? '';
		},

		/**
		 * Returns a PNG data URL for the given icon + color, suitable for use as
		 * a `PictureMarkerSymbol` URL. Mirrors exactly what the initial GeoJSON
		 * symbol creation does: rasterises the SVG icon through a canvas so the
		 * result is a proper `data:image/png;base64,...` string that Esri accepts.
		 * Returns an empty string when the loader is not yet initialised.
		 */
		async getIconDataUrl(icon: string, color: string, disableBackground?: boolean): Promise<string> {
			const loader = symbolLoaderRef.current;
			if (!loader) return '';
			return pictureDataUrlFromSymbolLoader(
				loader,
				SymbolLoader.createSymbolId(icon, color, undefined, undefined, disableBackground),
			);
		},
		
		async getAttributesList(
			layerId: string,
		): Promise<CBAttributes[] | undefined> {
			return configAdaptorRef.current?.getAttributesList(layerId);
		},

		getBaseLayerConfig(): ConfigLayer[] {
			return configAdaptorRef.current?.convertForPanel() ?? [];
		},

		editLayer,
		clearAllLayerEdits,
		setConfigVisibility,
		getTileColorExpression,
		isTileLookupPending,
		setOnLookupComplete,
		get symbolLoader(): SymbolLoader | null {
			return symbolLoaderRef.current;
		},
	};
};

export type Layers = ReturnType<typeof useLayers>;
